/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

const themeConfig = {
  /* ── Layout styles (independent of colour) ── */
  styles: [
    { id: 'standard',       label: 'Standard',       icon: '📐', description: 'Default Tailwind-like layout' },
    { id: 'ai',             label: 'AI Expert',      icon: '🤖', description: 'Material Design aesthetic' },
    { id: 'command-center', label: 'Command Center', icon: '🖥️', description: 'Terminal-grade precision, zero-radius, monospace' },
    { id: 'aurora',         label: 'Aurora',         icon: '🌌', description: 'Flowing pill shapes, glow shadows, spacious' },
    { id: 'stealth',        label: 'Stealth',        icon: '🔱', description: 'Luxury minimal — matte surfaces, subtle detail' },
  ],

  /* ── Colour schemes (independent of layout style) ── */
  colorSchemes: [
    { id: 'light',          label: 'Light',          icon: '☀️', isDark: false },
    { id: 'dark',           label: 'Dark',           icon: '🌙', isDark: true  },
    { id: 'ai-light',      label: 'AI Light',       icon: '🎨', isDark: false },
    { id: 'ai-dark',       label: 'AI Dark',        icon: '🧠', isDark: true  },
    { id: 'command-center', label: 'Command Center', icon: '💚', isDark: true  },
    { id: 'aurora',         label: 'Aurora',         icon: '🌌', isDark: true  },
    { id: 'stealth',        label: 'Stealth',        icon: '⚜️', isDark: true  },
  ],

  /* ── JS-accessible colour palettes (keyed by colorScheme id) ── */
  colors: {
    light: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      secondary: '#93c5fd',
      secondaryHover: '#60a5fa',
      accent: '#28a745',
      backgroundBody: '#f8fafc',
      backgroundSurface: '#ffffff',
      backgroundInput: '#f1f5f9',
      textPrimary: '#1e293b',
      textSecondary: '#6b7280',
      textAccent: '#ffffff',
      borderDefault: '#e5e7eb',
      destructive: '#ef4444',
      destructiveHover: '#b91c1c',
      success: '#22c55e',
      successHover: '#15803d',
      warning: '#f59e0b',
      info: '#3b82f6',
    },
    dark: {
      primary: '#3b82f6',
      primaryHover: '#2563eb',
      secondary: '#60a5fa',
      secondaryHover: '#3b82f6',
      accent: '#16a34a',
      backgroundBody: '#111827',
      backgroundSurface: '#1f2937',
      backgroundInput: '#111827',
      textPrimary: '#f3f4f6',
      textSecondary: '#9ca3af',
      textAccent: '#ffffff',
      borderDefault: '#374151',
      destructive: '#dc2626',
      destructiveHover: '#991b1b',
      success: '#15803d',
      successHover: '#14532d',
      warning: '#d97706',
      info: '#2563eb',
    },
    'ai-light': {
      primary: '#1976d2',
      primaryHover: '#1565c0',
      secondary: '#42a5f5',
      secondaryHover: '#1976d2',
      accent: '#dc004e',
      backgroundBody: '#f5f5f5',
      backgroundSurface: '#ffffff',
      backgroundInput: '#eeeeee',
      textPrimary: '#212121',
      textSecondary: '#757575',
      textAccent: '#ffffff',
      borderDefault: '#e0e0e0',
      destructive: '#ef4444',
      destructiveHover: '#b91c1c',
      success: '#4caf50',
      successHover: '#388e3c',
      warning: '#ff9800',
      info: '#2196f3',
    },
    'ai-dark': {
      primary: '#90caf9',
      primaryHover: '#42a5f5',
      secondary: '#2c3e50',
      secondaryHover: '#3a5068',
      accent: '#f48fb1',
      backgroundBody: '#121212',
      backgroundSurface: '#1e1e1e',
      backgroundInput: '#121212',
      textPrimary: '#ffffff',
      textSecondary: 'rgba(255, 255, 255, 0.7)',
      textAccent: '#ffffff',
      borderDefault: '#333333',
      destructive: '#ef5350',
      destructiveHover: '#c62828',
      success: '#66bb6a',
      successHover: '#388e3c',
      warning: '#ffa726',
      info: '#42a5f5',
    },
    /* ── Command Center: neon-green on near-black ── */
    'command-center': {
      primary: '#00ff88',
      primaryHover: '#00cc6a',
      secondary: '#0ea5e9',
      secondaryHover: '#0284c7',
      accent: '#f43f5e',
      backgroundBody: '#0a0e17',
      backgroundSurface: '#111827',
      backgroundInput: '#0f172a',
      textPrimary: '#e0f2fe',
      textSecondary: '#64748b',
      textAccent: '#0a0e17',
      borderDefault: '#1e293b',
      destructive: '#f43f5e',
      destructiveHover: '#e11d48',
      success: '#00ff88',
      successHover: '#00cc6a',
      warning: '#facc15',
      info: '#38bdf8',
      sidebar: '#0f172a',
      sidebarText: '#00ff88',
    },
    /* ── Aurora: vibrant purple / teal / pink ── */
    'aurora': {
      primary: '#a855f7',
      primaryHover: '#9333ea',
      secondary: '#14b8a6',
      secondaryHover: '#0d9488',
      accent: '#f472b6',
      backgroundBody: '#0f0b1e',
      backgroundSurface: '#1a1333',
      backgroundInput: '#150e2b',
      textPrimary: '#f0e7ff',
      textSecondary: '#a78bfa',
      textAccent: '#ffffff',
      borderDefault: '#2e1f5e',
      destructive: '#fb7185',
      destructiveHover: '#f43f5e',
      success: '#34d399',
      successHover: '#10b981',
      warning: '#fbbf24',
      info: '#22d3ee',
      sidebar: '#150e2b',
      sidebarText: '#c4b5fd',
    },
    /* ── Stealth: amber/gold on deep black ── */
    'stealth': {
      primary: '#f59e0b',
      primaryHover: '#d97706',
      secondary: '#292524',
      secondaryHover: '#44403c',
      accent: '#fbbf24',
      backgroundBody: '#09090b',
      backgroundSurface: '#18181b',
      backgroundInput: '#09090b',
      textPrimary: '#fafafa',
      textSecondary: '#a1a1aa',
      textAccent: '#09090b',
      borderDefault: '#27272a',
      destructive: '#ef4444',
      destructiveHover: '#dc2626',
      success: '#22c55e',
      successHover: '#16a34a',
      warning: '#f59e0b',
      info: '#a78bfa',
      sidebar: '#09090b',
      sidebarText: '#f59e0b',
    },
  },

  // Legacy compat: combined theme list (old consumers can still iterate)
  themes: [
    { id: 'light',    label: 'Light',        group: 'default', icon: '☀️' },
    { id: 'dark',     label: 'Dark',         group: 'default', icon: '🌙' },
    { id: 'ai-light', label: 'AI Light',     group: 'ai',      icon: '🤖' },
    { id: 'ai-dark',  label: 'AI Dark',      group: 'ai',      icon: '🧠' },
  ],
  fonts: {
    primary: 'Arial, sans-serif',
    secondary: 'Roboto, sans-serif',
  },
  spacing: {
    small: '0.5rem',
    medium: '1rem',
    large: '1.5rem',
  },
  breakpoints: {
    xs: '400px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
};

export default themeConfig;
